import { billingLabel, formatInt, moduleCount, money, seatsUsed, type Tenant, type TenantStatus, type TenantUser } from '@titan/shared';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ScreenBody, ScreenHeader } from '../../shell/Shell.js';
import {
  Button,
  ChipSelector,
  EmptyState,
  InlineError,
  KpiCard,
  KpiStrip,
  ListContainer,
  ListRow,
  Pill,
  PillRow,
  PrimaryCell,
  RowFigure,
  RowMeta,
  RowTitle,
  SecondaryCell,
  planTone,
  tenantStatusTone,
} from '../../ui/index.js';
import { useLoad } from '../useLoad.js';

interface TenantsResponse {
  tenants: Tenant[];
  users: Record<string, TenantUser[]>;
}

type Filter = 'All' | TenantStatus;

/** Spec 07 Tenants. Every KPI derives from the lists on this screen. */
export function Tenants() {
  const navigate = useNavigate();
  const { data, error, loading } = useLoad<TenantsResponse>('/admin/tenants');
  const [filter, setFilter] = useState<Filter>('All');

  const tenants = data?.tenants ?? [];
  const users = data?.users ?? {};
  const active = tenants.filter((t) => t.status === 'Active').length;
  const onboarding = tenants.filter((t) => t.status === 'Onboarding').length;
  const suspended = tenants.filter((t) => t.status === 'Suspended').length;
  const clientUsers = tenants.reduce((n, t) => n + seatsUsed(users[t.id] ?? []), 0);
  const seats = tenants.reduce((n, t) => n + t.seats, 0);
  const accounts = tenants.reduce((n, t) => n + t.accounts.length, 0);
  const mrr = tenants.reduce((n, t) => n + t.mrr, 0);
  const visible = filter === 'All' ? tenants : tenants.filter((t) => t.status === filter);

  return (
    <>
      <ScreenHeader
        eyebrow="administration · tenants"
        title="Tenants"
        description="Every client organization on the hub: their plan, what they are entitled to, and who runs them. The client owns their people; DinoCloud owns the entitlements."
        actions={
          <Button icon={<Plus size={15} strokeWidth={1.75} aria-hidden="true" />} onClick={() => navigate('/admin/tenants/new')}>
            New tenant
          </Button>
        }
      />
      <ScreenBody>
        {error && <InlineError>{error}</InlineError>}
        <KpiStrip min={190}>
          <KpiCard label="Tenants" figure={formatInt(tenants.length)} note={`${active} active · ${onboarding} onboarding · ${suspended} suspended`} />
          <KpiCard label="Client users" figure={formatInt(clientUsers)} note={`across ${formatInt(seats)} contracted seats`} />
          <KpiCard label="AWS accounts managed" figure={formatInt(accounts)} note="Linked through the DinoCloud role" />
          <KpiCard label="Recurring revenue" figure={money(mrr)} tone="positive" note="Per month, active contracts" />
        </KpiStrip>

        <ChipSelector<Filter>
          aria-label="Filter tenants"
          options={[
            { value: 'All', label: 'All' },
            { value: 'Active', label: 'Active' },
            { value: 'Onboarding', label: 'Onboarding' },
            { value: 'Suspended', label: 'Suspended' },
          ]}
          value={filter}
          onChange={setFilter}
        />

        <ListContainer>
          {!loading && visible.length === 0 && (
            <EmptyState>{tenants.length === 0 ? 'No tenants yet. Create the first one with New tenant.' : `No ${filter.toLowerCase()} tenants right now.`}</EmptyState>
          )}
          {visible.map((t) => {
            const list = users[t.id] ?? [];
            return (
              <ListRow key={t.id} clickable onClick={() => navigate(`/admin/tenants/${t.id}`)} onKeyDown={(e) => e.key === 'Enter' && navigate(`/admin/tenants/${t.id}`)}>
                <PrimaryCell>
                  <PillRow>
                    <Pill tone={tenantStatusTone(t.status)}>{t.status}</Pill>
                    <Pill tone={planTone(t.plan)} variant="tier">
                      {t.plan}
                    </Pill>
                  </PillRow>
                  <RowTitle>{t.name}</RowTitle>
                  <RowMeta>
                    {t.industry} · client since {t.since} · CSM {t.csm}
                  </RowMeta>
                </PrimaryCell>
                <SecondaryCell size="lg" right>
                  <RowFigure small>
                    {seatsUsed(list)} of {t.seats} seats
                  </RowFigure>
                  <RowMeta>
                    {t.accounts.length} {t.accounts.length === 1 ? 'account' : 'accounts'}
                  </RowMeta>
                  <RowMeta>{moduleCount(t)} of 6 modules enabled</RowMeta>
                  <RowMeta>{billingLabel(t, money)}</RowMeta>
                </SecondaryCell>
              </ListRow>
            );
          })}
        </ListContainer>
      </ScreenBody>
    </>
  );
}
